/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { FormattedMessage } from 'react-intl';
import { isEmpty, reduce, get, sortBy, sumBy } from 'lodash';
import { Loader, Grid } from 'semantic-ui-react';
import { format } from 'date-fns';

import { Table } from '../../../../components';
import ServiceReport from '../../containers/service/ServiceReport';
import ServiceReportSummary from '../../containers/service/ServiceReportSummary';

const DATE_FORMAT = 'yyyy-MM-dd';

export const GET_REPORT_QUERY = gql`
    query getReport(
        $salon: ID!
        $fromDate: String!
        $toDate: String!
        $showArchive: Boolean
        $lang: String!
        $showAppRecords: Boolean
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    serviceGroups {
                        nodes {
                            id
                            services {
                                id
                                name(lang: $lang)
                                report(
                                    fromDate: $fromDate
                                    toDate: $toDate
                                    salon: $salon
                                    showArchive: $showArchive
                                    showAppRecords: $showAppRecords
                                ) {
                                    ...report
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    fragment report on Report {
        invoices {
            id
            invoiceId
            createdAt
            invoiceType
            archived
            appointments {
                id
                client {
                    id
                    firstName
                    lastName
                    phone
                }
                master {
                    id
                    firstName
                    lastName
                    phone
                }
                duration
                authorRole
            }
            items {
                id
                price
                quantity
                type
                paymentType
                entity {
                    ... on Service {
                        id
                        name(lang: $lang)
                    }
                }
                sellerObject {
                    ... on Administrator {
                        id
                        firstName
                        lastName
                    }
                    ... on Master {
                        id
                        firstName
                        lastName
                    }
                    ... on Cabinet {
                        id
                        name(lang: $lang)
                    }
                }
            }
        }
    }
`;

const ServiceReportCell = styled(Table.Cell)`
    padding: 0px !important;
`;

const EditInvoiceHeaderCell = styled(Table.HeaderCell)`
    width: 62px;
`;

const EmptyMessage = ({ columns }) => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={columns} textAlign="center">
            <FormattedMessage
                id="pages.report.ReportTable.noReports"
                defaultMessage="There is no reports today"
            />
        </Table.Cell>
    </Table.Row>
);

const ReportTable = ({
    services,
    displayEditButton,
    sellerReportCellColSpan,
    hasReports,
}) => (
    <Table celled fixed>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.report.ReportTable.date"
                        defaultMessage="Date"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.report.ReportTable.client"
                        defaultMessage="Client"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.report.ReportTable.seller"
                        defaultMessage="Seller"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="center">
                    <FormattedMessage
                        id="pages.report.ReportTable.quantity"
                        defaultMessage="Quantity"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.report.ReportTable.price"
                        defaultMessage="Price"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                    <FormattedMessage
                        id="pages.report.ReportTable.total"
                        defaultMessage="Total"
                    />
                </Table.HeaderCell>
                {displayEditButton && <EditInvoiceHeaderCell />}
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {hasReports ? (
                services.map(
                    service =>
                        !isEmpty(service.report.invoices) && (
                            <Table.Row key={service.id}>
                                <ServiceReportCell
                                    colSpan={sellerReportCellColSpan}
                                >
                                    <ServiceReport
                                        service={service}
                                        displayEditButton={displayEditButton}
                                    />
                                </ServiceReportCell>
                            </Table.Row>
                        ),
                )
            ) : (
                <EmptyMessage columns={sellerReportCellColSpan} />
            )}
        </Table.Body>
    </Table>
);

const ServiceReportTableView = ({
    services,
    displayEditButton,
    sellerReportCellColSpan,
    hasReports,
}) => (
    <Grid columns={1} padded>
        <Grid.Row>
            <Grid.Column>
                <ServiceReportSummary services={services} />
            </Grid.Column>
        </Grid.Row>
        <Grid.Row>
            <Grid.Column>
                <ReportTable
                    services={services}
                    displayEditButton={displayEditButton}
                    sellerReportCellColSpan={sellerReportCellColSpan}
                    hasReports={hasReports}
                />
            </Grid.Column>
        </Grid.Row>
    </Grid>
);

function ServiceReportTable() {
    const salon = useSelector(({ user }) => user.get('salon'));
    const isOwner = useSelector(({ user }) => user.get('isOwner'));
    const fromDate = useSelector(({ report }) =>
        format(new Date(report.get('fromDate')), DATE_FORMAT),
    );
    const toDate = useSelector(({ report }) =>
        format(new Date(report.get('toDate')), DATE_FORMAT),
    );
    const showArchive = useSelector(({ salon }) => salon.get('showArchive'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );
    const showAppRecords = useSelector(({ report }) =>
        report.get('showAppRecords'),
    );

    const { loading, data } = useQuery(GET_REPORT_QUERY, {
        variables: {
            salon,
            fromDate,
            toDate,
            showArchive,
            lang: currentLanguage,
            showAppRecords,
        },
        fetchPolicy: 'cache-and-network',
    });

    if (loading) return <Loader size="big" inline="centered" active />;

    const services =
        !loading && data && data.viewer
            ? reduce(
                  get(data.viewer, 'salon.serviceGroups.nodes'),
                  (result, item) => [...result, ...item.services],
                  [],
              )
            : [];

    const displayEditButton = isOwner;
    const sellerReportCellColSpan = isOwner ? 7 : 6;
    const hasReports = !!reduce(
        services,
        (sum, service) => sum + get(service, 'report.invoices', []).length,
        0,
    );
    const sortedServices = sortBy(
        services,
        service =>
            0 -
            reduce(
                get(service, 'report.invoices', []),
                (result, invoice) =>
                    result +
                    sumBy(
                        get(invoice, 'items', []),
                        ({ price = 0, quantity = 0 }) => price * quantity,
                    ),
                0,
            ),
    );

    return (
        <ServiceReportTableView
            services={sortedServices}
            displayEditButton={displayEditButton}
            sellerReportCellColSpan={sellerReportCellColSpan}
            hasReports={hasReports}
        />
    );
}

export default ServiceReportTable;
